import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { config } from '../../../config';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker (self-hosted for privacy and reliability)
pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.min.js';

@Component({
  selector: 'app-cv-viewer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="cv-viewer-modal" *ngIf="isVisible">
      <div class="modal-backdrop" (click)="onClose()"></div>
      <div class="modal-content cv-viewer-content">
        <div class="modal-header">
          <h3>{{ fileName || 'CV Viewer' }}</h3>
          <div class="header-actions">
            <!-- PDF Navigation Controls -->
            <div class="pdf-controls" *ngIf="isPDFViewerMode && totalPages > 0">
              <button class="btn-nav" (click)="previousPage()" [disabled]="currentPage <= 1">‹</button>
              <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
              <button class="btn-nav" (click)="nextPage()" [disabled]="currentPage >= totalPages">›</button>
              <button class="btn-zoom" (click)="zoomOut()" [disabled]="currentScale <= 0.5">-</button>
              <span class="zoom-info">{{ Math.round(currentScale * 100) }}%</span>
              <button class="btn-zoom" (click)="zoomIn()" [disabled]="currentScale >= 3.0">+</button>
            </div>
            
            <button class="btn-download" (click)="downloadCV()" [disabled]="!contactId">
              Download
            </button>
            <button class="close-btn" (click)="onClose()">&times;</button>
          </div>
        </div>
        
        <div class="modal-body">
          <!-- PDF.js Viewer -->
          <div *ngIf="isPDFViewerMode" class="pdf-viewer-container">
            <canvas #pdfCanvas class="pdf-canvas"></canvas>
          </div>
          
          <!-- Fallback iframe for direct PDF viewing -->
          <iframe *ngIf="isPDF && !isPDFViewerMode && viewerUrl" 
                  [src]="viewerUrl" 
                  class="pdf-viewer"
                  frameborder="0">
          </iframe>
          
          <!-- Document Download Message -->
          <div *ngIf="isDocument" class="document-message">
            <div class="message-content">
              <h4>{{ documentMessageTitle }}</h4>
              <p>{{ fileName }} {{ documentMessage }}</p>
              <div class="download-actions">
                <button class="btn-download-large" (click)="downloadCV()">
                  Download to View
                </button>
              </div>
            </div>
          </div>
          
          <!-- Loading State -->
          <div *ngIf="isLoading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>{{ loadingMessage }}</p>
          </div>
          
          <!-- Error State -->
          <div *ngIf="error" class="error-state">
            <h4>Unable to Load CV</h4>
            <p>{{ error }}</p>
            <div class="error-actions">
              <button class="btn-retry" (click)="loadCV()">Try Again</button>
              <button class="btn-download" (click)="downloadCV()">{{ fallbackMessage }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .cv-viewer-modal {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 3000;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .modal-backdrop {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(13,18,30,.6);
    }

    .cv-viewer-content {
      background: var(--ap-surface, #fff);
      border-radius: var(--ap-radius-lg, 14px);
      width: 90vw;
      height: 90vh;
      max-width: 1200px;
      max-height: 800px;
      border: 1px solid var(--ap-border, #E3E6EB);
      position: relative;
      z-index: 3001;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 14px 20px;
      border-bottom: 1px solid var(--ap-border, #E3E6EB);
      background: var(--ap-surface-muted, #EEF0F3);
      border-radius: 14px 14px 0 0;
    }

    .modal-header h3 {
      margin: 0;
      color: var(--ap-text, #0B1220);
      font-size: 15px;
      font-weight: 600;
    }

    .header-actions {
      display: flex;
      gap: 8px;
      align-items: center;
      flex-wrap: wrap;
    }

    .pdf-controls {
      display: flex;
      gap: 4px;
      align-items: center;
      margin-right: 8px;
      padding: 3px 8px;
      background: var(--ap-surface, #fff);
      border-radius: var(--ap-radius-sm, 6px);
      border: 1px solid var(--ap-border, #E3E6EB);
    }

    .btn-nav, .btn-zoom {
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
      border: none;
      padding: 3px 7px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
      min-width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s;
    }

    .btn-nav:hover:not(:disabled), .btn-zoom:hover:not(:disabled) {
      background: var(--ap-primary-ink, #1E3FA8);
    }

    .btn-nav:disabled, .btn-zoom:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .page-info, .zoom-info {
      font-size: 12px;
      color: var(--ap-text-soft, #4B5563);
      font-weight: 500;
      min-width: 45px;
      text-align: center;
    }

    .btn-download {
      background: var(--ap-success, #047A55);
      color: #fff;
      border: none;
      padding: 0 12px;
      height: 30px;
      border-radius: var(--ap-radius-sm, 6px);
      cursor: pointer;
      font-size: 12px;
      font-weight: 550;
      transition: background 0.15s;
    }

    .btn-download:hover:not(:disabled) {
      background: #035C40;
    }

    .btn-download:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 18px;
      cursor: pointer;
      color: var(--ap-text-muted, #6B7280);
      padding: 0;
      line-height: 1;
      width: 28px;
      height: 28px;
      border-radius: var(--ap-radius-sm, 6px);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s;
    }

    .close-btn:hover {
      background: var(--ap-border, #E3E6EB);
      color: var(--ap-text, #0B1220);
    }

    .modal-body {
      flex: 1;
      padding: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .pdf-viewer {
      width: 100%;
      height: 100%;
      border: none;
    }

    .pdf-viewer-container {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      overflow: auto;
      background: var(--ap-bg, #F6F7F9);
      padding: 20px;
    }

    .pdf-canvas {
      box-shadow: var(--ap-shadow, 0 4px 12px rgba(13,18,30,.10));
      background: var(--ap-surface, #fff);
      max-width: 100%;
      height: auto;
    }

    .document-message {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px;
      text-align: center;
      background: var(--ap-bg, #F6F7F9);
    }

    .message-content h4 {
      color: var(--ap-text-soft, #4B5563);
      margin-bottom: 12px;
      font-weight: 600;
    }

    .message-content p {
      color: var(--ap-text-muted, #6B7280);
      margin-bottom: 8px;
      line-height: 1.5;
      font-size: 13px;
    }

    .download-actions {
      margin-top: 20px;
    }

    .btn-download-large {
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
      border: none;
      padding: 0 24px;
      height: 36px;
      border-radius: var(--ap-radius-sm, 6px);
      cursor: pointer;
      font-size: 14px;
      font-weight: 550;
      transition: background 0.15s;
    }

    .btn-download-large:hover {
      background: var(--ap-primary-ink, #1E3FA8);
    }

    .loading-state {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px;
    }

    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(47,91,218,.2);
      border-top: 3px solid var(--ap-primary, #2F5BDA);
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: 16px;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .loading-state p {
      color: var(--ap-text-muted, #6B7280);
      margin: 0;
      font-size: 13px;
    }

    .error-state {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px;
      text-align: center;
    }

    .error-state h4 {
      color: var(--ap-danger, #B42318);
      margin-bottom: 12px;
      font-weight: 600;
    }

    .error-state p {
      color: var(--ap-text-muted, #6B7280);
      margin-bottom: 24px;
      line-height: 1.5;
      font-size: 13px;
    }

    .error-actions {
      display: flex;
      gap: 8px;
    }

    .btn-retry {
      background: var(--ap-primary-soft, #E8EEFC);
      color: var(--ap-primary, #2F5BDA);
      border: 1px solid rgba(47,91,218,.2);
      padding: 0 14px;
      height: 32px;
      border-radius: var(--ap-radius-sm, 6px);
      cursor: pointer;
      font-size: 13px;
      font-weight: 550;
      transition: background 0.15s;
    }

    .btn-retry:hover {
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
    }
  `]
})
export class CVViewerComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input() isVisible: boolean = false;
  @Input() contactId: string | null = null;
  @Input() fileName: string | null = null;
  
  @Output() closeViewer = new EventEmitter<void>();
  
  @ViewChild('pdfCanvas', { static: false }) pdfCanvas!: ElementRef<HTMLCanvasElement>;
  
  // State management
  isLoading = false;
  error: string | null = null;
  isPDF = false;
  isDocument = false;
  isPDFViewerMode = false;
  
  // Messages
  documentMessage = '';
  documentMessageTitle = 'Document Preview Not Available';
  loadingMessage = 'Loading CV...';
  fallbackMessage = 'Download Instead';
  
  // PDF.js properties
  currentPdfDocument: any = null;
  currentPage = 1;
  totalPages = 0;
  currentScale = 1.2;
  isRendering = false;
  
  // Fallback properties
  viewerUrl: SafeResourceUrl | null = null;
  
  // Timeout and retry logic
  private loadTimeout: any = null;
  private maxRetries = 3;
  private currentRetries = 0;
  
  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) {}
  
  ngAfterViewInit(): void {
    // PDF.js initialization will happen in loadCV when needed
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && this.isVisible && this.contactId) {
      this.loadCV();
    }
    
    if (changes['isVisible'] && !this.isVisible) {
      this.resetViewer();
    }
  }
  
  ngOnDestroy(): void {
    this.clearTimeouts();
    this.cleanupPDFDocument();
  }
  
  async loadCV(): Promise<void> {
    if (!this.contactId) {
      this.error = 'No contact selected';
      return;
    }
    
    this.currentRetries = 0;
    await this.loadCVWithRetry();
  }
  
  private async loadCVWithRetry(): Promise<void> {
    this.clearTimeouts();
    this.resetViewerState();
    this.isLoading = true;
    this.loadingMessage = this.currentRetries > 0 ? `Retrying... (${this.currentRetries}/${this.maxRetries})` : 'Loading CV...';
    
    // Set timeout for the request
    this.loadTimeout = setTimeout(() => {
      this.error = 'Loading timed out. Please try again or download the file.';
      this.fallbackMessage = 'Download File';
      this.isLoading = false;
    }, 30000); // 30 second timeout
    
    try {
      // First, check what type of response we get
      const response = await this.http.get(
        `${config.apiUrl}/api/contacts/${this.contactId}/cv/view`,
        { 
          headers: { 'Cache-Control': 'no-cache' },
          observe: 'response',
          responseType: 'blob'
        }
      ).toPromise();
      
      this.clearTimeouts();
      
      // Check if response is a JSON error response or PDF blob
      const contentType = response?.headers.get('Content-Type');
      
      if (contentType?.includes('application/json')) {
        // Parse JSON response (likely an error or document message)
        const text = await response!.body!.text();
        const jsonResponse = JSON.parse(text);
        
        if (!jsonResponse.success) {
          throw new Error(jsonResponse.message || 'Failed to load CV information');
        }
        
        if (jsonResponse.data?.fileType === 'document') {
          // Document that needs to be downloaded
          this.handleDocumentResponse(jsonResponse.data);
          return;
        }
      } else if (contentType?.includes('application/pdf')) {
        // PDF blob response - handle PDF viewing
        await this.handlePDFResponseFromBlob(response!.body!);
        return;
      }
      
      // If we get here, something unexpected happened
      throw new Error('Unexpected response type from server');
      
    } catch (error: any) {
      this.clearTimeouts();
      await this.handleLoadError(error);
    } finally {
      this.isLoading = false;
    }
  }
  
  private handleDocumentResponse(data: any): void {
    this.isDocument = true;
    this.documentMessage = data.message || 'This document type must be downloaded to view.';
    this.documentMessageTitle = 'Download Required';
    
    if (data.message?.includes('conversion failed')) {
      this.documentMessageTitle = 'Conversion Failed';
      this.documentMessage = 'The document could not be converted for online viewing.';
    } else if (data.message?.includes('timeout')) {
      this.documentMessageTitle = 'Processing Timeout';
      this.documentMessage = 'Document processing took too long.';
    }
  }
  
  private async handlePDFResponseFromBlob(pdfBlob: Blob): Promise<void> {
    try {
      // Create blob URL for PDF.js
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Try PDF.js first for better control and features
      await this.loadPDFWithPDFjs(pdfUrl);
    } catch (pdfError) {
      console.warn('PDF.js failed, falling back to iframe:', pdfError);
      // Fallback to iframe with blob URL
      const pdfUrl = URL.createObjectURL(pdfBlob);
      this.isPDF = true;
      this.isPDFViewerMode = false;
      this.viewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);
    }
  }
  
  private async handlePDFResponse(): Promise<void> {
    const pdfUrl = `${config.apiUrl}/api/contacts/${this.contactId}/cv/view`;
    
    try {
      // Try PDF.js first for better control and features
      await this.loadPDFWithPDFjs(pdfUrl);
    } catch (pdfError) {
      console.warn('PDF.js failed, falling back to iframe:', pdfError);
      // Fallback to iframe
      this.isPDF = true;
      this.isPDFViewerMode = false;
      this.viewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);
    }
  }
  
  private async loadPDFWithPDFjs(url: string): Promise<void> {
    this.loadingMessage = 'Processing PDF...';
    
    try {
      // Load PDF document
      const loadingTask = pdfjsLib.getDocument({
        url: url,
        httpHeaders: {
          'Cache-Control': 'no-cache'
        }
      });
      
      this.currentPdfDocument = await loadingTask.promise;
      this.totalPages = this.currentPdfDocument.numPages;
      this.currentPage = 1;
      this.isPDFViewerMode = true;
      this.isPDF = true;
      
      // Render first page
      await this.renderPage(this.currentPage);
      
    } catch (error: any) {
      console.error('PDF.js loading failed:', error);
      throw new Error(`PDF loading failed: ${error.message}`);
    }
  }
  
  private async handleLoadError(error: any): Promise<void> {
    console.error('CV loading error:', error);
    
    // Determine error type and appropriate response
    if (error.status === 404) {
      this.error = 'CV not found for this contact';
      this.fallbackMessage = 'Check Contact';
    } else if (error.status === 403) {
      this.error = 'You do not have permission to view this CV';
      this.fallbackMessage = 'Contact Admin';
    } else if (error.status === 408 || error.message?.includes('timeout')) {
      this.error = 'CV processing timed out. The file may be too large or complex.';
      this.fallbackMessage = 'Download File';
    } else if (this.currentRetries < this.maxRetries) {
      // Retry on network or temporary errors
      this.currentRetries++;
      setTimeout(() => this.loadCVWithRetry(), 2000 * this.currentRetries); // Exponential backoff
      return;
    } else {
      this.error = 'Unable to load CV. Please try downloading the file.';
      this.fallbackMessage = 'Download File';
    }
    
    // Always provide fallback option
    if (error.data?.fallback) {
      this.fallbackMessage = error.data.fallback.message || 'Download to View';
    }
  }
  
  // PDF Navigation Methods
  async previousPage(): Promise<void> {
    if (this.currentPage > 1 && !this.isRendering) {
      this.currentPage--;
      await this.renderPage(this.currentPage);
    }
  }
  
  async nextPage(): Promise<void> {
    if (this.currentPage < this.totalPages && !this.isRendering) {
      this.currentPage++;
      await this.renderPage(this.currentPage);
    }
  }
  
  async zoomIn(): Promise<void> {
    if (this.currentScale < 3.0 && !this.isRendering) {
      this.currentScale += 0.2;
      await this.renderPage(this.currentPage);
    }
  }
  
  async zoomOut(): Promise<void> {
    if (this.currentScale > 0.5 && !this.isRendering) {
      this.currentScale -= 0.2;
      await this.renderPage(this.currentPage);
    }
  }
  
  // Utility method for Math.round in template
  Math = Math;
  
  // PDF Rendering
  private async renderPage(pageNumber: number): Promise<void> {
    if (!this.currentPdfDocument || this.isRendering) return;
    
    this.isRendering = true;
    
    try {
      const page = await this.currentPdfDocument.getPage(pageNumber);
      const canvas = this.pdfCanvas.nativeElement;
      const context = canvas.getContext('2d');
      
      // Calculate viewport with current scale
      const viewport = page.getViewport({ scale: this.currentScale });
      
      // Set canvas dimensions
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      // Render page
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };
      
      await page.render(renderContext).promise;
      
    } catch (error) {
      console.error('Error rendering PDF page:', error);
      this.error = 'Failed to render PDF page';
    } finally {
      this.isRendering = false;
    }
  }
  
  async downloadCV(): Promise<void> {
    if (!this.contactId) return;
    
    try {
      const response = await this.http.get(
        `${config.apiUrl}/api/contacts/${this.contactId}/cv`,
        { responseType: 'blob' }
      ).toPromise();
      
      const blob = new Blob([response!], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.fileName || 'cv.pdf';
      link.click();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading CV:', error);
      this.error = 'Failed to download CV';
    }
  }
  
  onClose(): void {
    this.resetViewer();
    this.closeViewer.emit();
  }
  
  // Cleanup Methods
  private clearTimeouts(): void {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }
  }
  
  private cleanupPDFDocument(): void {
    if (this.currentPdfDocument) {
      this.currentPdfDocument.destroy();
      this.currentPdfDocument = null;
    }
  }
  
  private resetViewerState(): void {
    this.error = null;
    this.isPDF = false;
    this.isDocument = false;
    this.isPDFViewerMode = false;
    this.documentMessage = '';
    this.documentMessageTitle = 'Document Preview Not Available';
    this.loadingMessage = 'Loading CV...';
    this.fallbackMessage = 'Download Instead';
    this.viewerUrl = null;
    this.currentPage = 1;
    this.totalPages = 0;
    this.currentScale = 1.2;
    this.isRendering = false;
    this.cleanupPDFDocument();
  }
  
  private resetViewer(): void {
    this.clearTimeouts();
    this.resetViewerState();
    this.isLoading = false;
    this.currentRetries = 0;
  }
}